import React from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {vscDarkPlus} from 'react-syntax-highlighter/dist/esm/styles/prism';
import { MOB_EVENT_TYPE_EVENT } from '../../config/const';
import { mob } from '../../utils/logger';

const MemoryApiGuideJavaScript = ({ onSectionChange }) => {
  return (
    <div className="doc-content">
      <div className="quickstart-header">
        <p className="quickstart-category">Getting Started</p>
        <h1>Memory API Guide - JavaScript</h1>
      </div>

      <p>Learn how to use MemU's Memory API for manual memory management and advanced use cases with JavaScript/Node.js.</p>

      <h2 id="overview">Overview</h2>
      <p>
        The Memory API provides granular control over memory operations, allowing you to manually manage
        conversation storage and retrieval. This is ideal for advanced use cases where you need custom
        memory handling logic or want to integrate with existing JavaScript applications.
      </p>

      <div className="feature-highlight-box">
        <h4>🎯 What you'll learn</h4>
        <ul>
          <li>How to set up MemU Memory API in JavaScript</li>
          <li>Step-by-step memory operations workflow</li>
          <li>Best practices for memory management</li>
          <li>Integration with popular AI frameworks</li>
        </ul>
      </div>

      <h2 id="step-1-installation">Step 1: Installation and Setup</h2>
      
      <h3>Install the MemU JavaScript SDK</h3>
      <div className="code-block">
        <SyntaxHighlighter
          language="bash"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          npm install memu-js
        </SyntaxHighlighter>
      </div>

      <h3>Get your API key</h3>
      <div className="setup-steps">
        <div className="step-item">
          <div className="step-number">1</div>
          <div className="step-content">
            <p>Visit <a href="https://app.memu.so/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => mob(
                'memu_pro_ev_quick_start_to_console_click',
                MOB_EVENT_TYPE_EVENT,
                {
                  source: 'memory',
                  source_lang: 'js'
                },
                true)
              }>https://app.memu.so/</a> and sign in</p>
          </div>
        </div>
        <div className="step-item">
          <div className="step-number">2</div>
          <div className="step-content">
            <p>Copy your API key from the dashboard</p>
          </div>
        </div>
        <div className="step-item">
          <div className="step-number">3</div>
          <div className="step-content">
            <p>Set it as an environment variable:</p>
            <div className="code-block">
              <SyntaxHighlighter
                language="bash"
                style={vscDarkPlus}
                customStyle={{
                  margin: 0,
                  padding: 0,
                  background: 'transparent',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}
              >
                export MEMU_API_KEY="your_api_key_here"
              </SyntaxHighlighter>
            </div>
          </div>
        </div>
      </div>

      <h2 id="step-2-initialize">Step 2: Initialize the Client</h2>
      
      <div className="code-block">
        <SyntaxHighlighter
          language="javascript"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`import { MemuClient } from 'memu-js';

// Initialize MemU client
const memuClient = new MemuClient({
    baseUrl: "https://api.memu.so",
    apiKey: process.env.MEMU_API_KEY
});

console.log("✅ MemU client initialized successfully!");`}
        </SyntaxHighlighter>
      </div>

      <h2 id="step-3-workflow">Step 3: Understanding the Memory Workflow</h2>
      
      <div className="key-points-box">
        <h4>🔄 Memory API Workflow</h4>
        <p>The Memory API follows this pattern for optimal performance:</p>
        <ol>
          <li><strong>Retrieve</strong> existing memories before starting a conversation</li>
          <li><strong>Build context</strong> using retrieved memories</li>
          <li><strong>Process</strong> user input with your AI model</li>
          <li><strong>Memorize</strong> the conversation for future use</li>
        </ol>
      </div>

      <h2 id="step-4-retrieve-memories">Step 4: Retrieve Existing Memories</h2>
      
      <p>Before starting a conversation, retrieve existing user memories to build context:</p>

      <div className="code-block">
        <SyntaxHighlighter
          language="javascript"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`// Step 4: Retrieve user memories
async function retrieveUserMemories(userId, agentId) {
    try {
        const memories = await memuClient.retrieveDefaultCategories({
            userId: userId,
            agentId: agentId
        });
        
        console.log('📚 Retrieved memories:', memories);
        return memories;
    } catch (error) {
        console.error('❌ Error retrieving memories:', error);
        return { categories: [] };
    }
}

// Example usage
const userId = "user_123";
const agentId = "assistant_001";
const userMemories = await retrieveUserMemories(userId, agentId);`}
        </SyntaxHighlighter>
      </div>

      <h2 id="step-5-build-context">Step 5: Build AI Context with Memories</h2>
      
      <p>Use retrieved memories to create a personalized system prompt:</p>

      <div className="code-block">
        <SyntaxHighlighter
          language="javascript"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`// Step 5: Build context from memories
function buildSystemPrompt(memories) {
    let systemPrompt = "You are a helpful AI assistant. Here's what you know about the user:\\n\\n";
    
    if (memories && memories.categories) {
        memories.categories.forEach(category => {
            if (category.summary) {
                systemPrompt += \`**\${category.name}:** \${category.summary}\\n\\n\`;
            }
        });
    }
    
    return systemPrompt;
}

// Example usage
const systemPrompt = buildSystemPrompt(userMemories);
console.log('🧠 System prompt built:', systemPrompt);`}
        </SyntaxHighlighter>
      </div>

      <h2 id="step-6-ai-interaction">Step 6: Process User Input with AI</h2>
      
      <p>Now use the context to have a conversation. Here's an example with OpenAI:</p>

      <div className="code-block">
        <SyntaxHighlighter
          language="javascript"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`// Step 6: AI interaction with context
import OpenAI from 'openai';

const openai = new OpenAI({
    apiKey: process.env.OPENAI_API_KEY
});

async function chatWithAI(systemPrompt, userMessage, conversationHistory = []) {
    try {
        const messages = [
            { role: "system", content: systemPrompt },
            ...conversationHistory,
            { role: "user", content: userMessage }
        ];

        const completion = await openai.chat.completions.create({
            model: "gpt-4o",
            messages: messages
        });

        const aiResponse = completion.choices[0].message.content;
        console.log('🤖 AI Response:', aiResponse);
        
        return aiResponse;
    } catch (error) {
        console.error('❌ Error with AI:', error);
        return "Sorry, I encountered an error.";
    }
}

// Example usage
const userMessage = "Hi, how are you today?";
const aiResponse = await chatWithAI(systemPrompt, userMessage);`}
        </SyntaxHighlighter>
      </div>

      <h2 id="step-7-memorize">Step 7: Save Conversation to Memory</h2>
      
      <p>After the conversation, save it to MemU for future use:</p>

      <div className="code-block">
        <SyntaxHighlighter
          language="javascript"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`// Step 7: Save conversation to memory
async function saveConversation(conversation, userId, agentId) {
    try {
        const response = await memuClient.memorizeConversation({
            conversation: conversation,
            userId: userId,
            userName: "Demo User",
            agentId: agentId,
            agentName: "AI Assistant"
        });

        console.log('💾 Conversation saved! Task ID:', response.taskId);
        
        // Wait for completion
        await waitForCompletion(response.taskId);
        
    } catch (error) {
        console.error('❌ Error saving conversation:', error);
    }
}

async function waitForCompletion(taskId) {
    while (true) {
        try {
            const status = await memuClient.getTaskStatus(taskId);
            console.log('📊 Task status:', status.status);
            
            if (['SUCCESS', 'FAILURE', 'REVOKED'].includes(status.status)) {
                break;
            }
            
            // Wait 2 seconds before checking again
            await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (error) {
            console.error('❌ Error checking task status:', error);
            break;
        }
    }
}

// Example: Build conversation context for saving
const conversationContext = [
    { role: "user", content: userMessage },
    { role: "assistant", content: aiResponse }
];

await saveConversation(conversationContext, userId, agentId);`}
        </SyntaxHighlighter>
      </div>

      <h2 id="step-8-complete-example">Step 8: Complete Interactive Example</h2>
      
      <p>Here's a complete example that puts it all together:</p>

      <div className="code-block">
        <SyntaxHighlighter
          language="javascript"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`// Complete Memory API Example
import { MemuClient } from 'memu-js';
import OpenAI from 'openai';
import readline from 'readline';

// Initialize clients
const memuClient = new MemuClient({
    baseUrl: "https://api.memu.so",
    apiKey: process.env.MEMU_API_KEY
});

const openai = new OpenAI({
    apiKey: process.env.OPENAI_API_KEY
});

// Create readline interface
const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout
});

async function runMemoryChat() {
    const userId = "user_123";
    const agentId = "assistant_001";
    
    // Step 1: Retrieve existing memories
    console.log('🔍 Retrieving user memories...');
    const userMemories = await memuClient.retrieveDefaultCategories({
        userId: userId,
        agentId: agentId
    });
    
    // Step 2: Build system prompt with memories
    let systemPrompt = "You are a helpful AI assistant. Here's what you know about the user:\\n\\n";
    
    if (userMemories && userMemories.categories) {
        userMemories.categories.forEach(category => {
            if (category.summary) {
                systemPrompt += \`**\${category.name}:** \${category.summary}\\n\\n\`;
            }
        });
    }
    
    console.log('🧠 Context built with user memories');
    
    // Step 3: Start interactive conversation
    const conversationHistory = [];
    
    const chatLoop = () => {
        rl.question('👤 You: ', async (userInput) => {
            if (userInput.toLowerCase() === 'quit') {
                console.log('👋 Goodbye!');
                rl.close();
                return;
            }
            
            try {
                // Step 4: Get AI response with context
                const messages = [
                    { role: "system", content: systemPrompt },
                    ...conversationHistory,
                    { role: "user", content: userInput }
                ];
                
                const completion = await openai.chat.completions.create({
                    model: "gpt-4o",
                    messages: messages
                });
                
                const aiResponse = completion.choices[0].message.content;
                console.log(\`🤖 Assistant: \${aiResponse}\`);
                
                // Step 5: Update conversation history
                conversationHistory.push(
                    { role: "user", content: userInput },
                    { role: "assistant", content: aiResponse }
                );
                
                // Step 6: Save to memory (every few exchanges to optimize)
                if (conversationHistory.length >= 4) {
                    console.log('💾 Saving conversation to memory...');
                    await memuClient.memorizeConversation({
                        conversation: conversationHistory,
                        userId: userId,
                        userName: "Demo User",
                        agentId: agentId,
                        agentName: "AI Assistant"
                    });
                    conversationHistory.length = 0; // Clear after saving
                }
                
            } catch (error) {
                console.error('❌ Error:', error);
            }
            
            chatLoop(); // Continue conversation
        });
    };
    
    console.log('🚀 Starting memory-powered chat (type "quit" to exit)');
    chatLoop();
}

// Run the example
runMemoryChat().catch(console.error);`}
        </SyntaxHighlighter>
      </div>

      <h2 id="best-practices">Best Practices</h2>
      
      <div className="key-points-box">
        <h4>💡 Memory API Best Practices</h4>
        <ul>
          <li><strong>Pre-conversation Retrieval:</strong> Always retrieve memories before starting a conversation</li>
          <li><strong>Batch Memorization:</strong> Save conversations in batches (every 2-4 exchanges) for better performance</li>
          <li><strong>Error Handling:</strong> Implement proper error handling for API calls</li>
          <li><strong>Context Management:</strong> Keep conversation history manageable to avoid token limits</li>
        </ul>
      </div>

      

      <h2 id="comparison">Memory API vs Response API</h2>
      
      <div className="comparison-box">
        <h4>📋 When to Use Memory API</h4>
        <div className="comparison-grid">
          <div className="comparison-column">
            <h5>Memory API (Manual Control)</h5>
            <ul>
              <li>✅ Full control over memory operations</li>
              <li>✅ Custom memory processing logic</li>
              <li>✅ Integration with existing AI models</li>
              <li>✅ Framework flexibility (OpenAI, Anthropic, etc.)</li>
              <li>✅ Advanced memory workflows</li>
            </ul>
          </div>
          <div className="comparison-column">
            <h5>Response API (Automatic)</h5>
            <ul>
              <li>✅ Automatic memory management</li>
              <li>✅ Simplified integration</li>
              <li>✅ Built-in AI model</li>
              <li>✅ Quick prototyping</li>
              <li>✅ No external AI API needed</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Need Help?</h2>
      <p>If you have any questions about the Memory API JavaScript integration, feel free to reach out:</p>

      <div className="help-links">
        <div className="help-item">
          <h3>Discord</h3>
          <p>Join our developer community</p>
          <a href="https://discord.gg/hQZntfGsbJ" className="help-link">Join Discord →</a>
        </div>

        <div className="help-item">
          <h3>GitHub</h3>
          <p>Browse examples and ask questions</p>
          <a href="#github" className="help-link">Visit GitHub →</a>
        </div>

        <div className="help-item">
          <h3>Support</h3>
          <p>Get direct technical support</p>
          <a href="mailto:info@nevamind.ai" className="help-link">Contact Support →</a>
        </div>
      </div>
    </div>
  );
};

export default MemoryApiGuideJavaScript;
